import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { eur, dateFr, DEVIS_STATUS, FACTURE_STATUS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { FileText, Users, ReceiptEuro, TrendingUp, Plus, AlertCircle } from "lucide-react";

const Metric = ({ icon: Icon, label, value, color, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-md p-5 flex items-start gap-4">
    <div className={`w-12 h-12 rounded-md grid place-items-center text-white shrink-0 ${color}`}>
      <Icon size={26} />
    </div>
    <div className="min-w-0">
      <div className="text-xs font-semibold tracking-wider uppercase text-slate-500">{label}</div>
      <div className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900 truncate">{value}</div>
    </div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const Badge = ({ map, status }) => (
    <span className={`text-xs font-semibold px-2 py-1 rounded ${map[status]?.cls || ""}`}>
      {map[status]?.label || status}
    </span>
  );

  return (
    <div className="p-4 md:p-8 space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
            Tableau de bord
          </h1>
          <p className="text-slate-600">Vue d'ensemble de votre activité</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="quick-new-client" onClick={() => navigate("/clients?new=1")} variant="outline" className="h-12 font-bold border-[#0284C7] text-[#0284C7]">
            <Users size={20} className="mr-1" /> Client
          </Button>
          <Button data-testid="quick-new-devis" onClick={() => navigate("/devis/new")} className="h-12 font-bold bg-[#16A34A] hover:bg-[#15803D]">
            <Plus size={20} className="mr-1" /> Nouveau devis
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
        <Metric testid="metric-devis-encours" icon={FileText} label="Devis en cours" value={data?.devis_en_cours ?? "—"} color="bg-[#0284C7]" />
        <Metric testid="metric-devis-acceptes" icon={TrendingUp} label="Devis acceptés" value={data?.devis_acceptes ?? "—"} color="bg-[#16A34A]" />
        <Metric testid="metric-factures-impayees" icon={AlertCircle} label="Factures impayées" value={data?.factures_impayees ?? "—"} color="bg-red-500" />
        <Metric testid="metric-ca" icon={ReceiptEuro} label="CA estimé" value={data ? eur(data.ca_estime) : "—"} color="bg-slate-900" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold">Derniers devis</h2>
            <Button variant="ghost" data-testid="see-all-devis" onClick={() => navigate("/devis")} className="text-[#0284C7] font-semibold">
              Tout voir
            </Button>
          </div>
          <div className="space-y-2">
            {(data?.recent_devis || []).length === 0 && <p className="text-slate-500 text-sm">Aucun devis.</p>}
            {(data?.recent_devis || []).map((d) => (
              <button
                key={d.id}
                data-testid={`dash-devis-${d.id}`}
                onClick={() => navigate(`/devis/${d.id}`)}
                className="w-full flex items-center justify-between p-3 rounded-md hover:bg-slate-50 text-left border border-slate-100"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{d.number}</div>
                  <div className="text-sm text-slate-500 truncate">{d.client?.name || "—"} · {dateFr(d.date)}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge map={DEVIS_STATUS} status={d.status} />
                  <span className="font-bold text-slate-900">{eur(d.total_ttc)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold">Dernières factures</h2>
            <Button variant="ghost" data-testid="see-all-factures" onClick={() => navigate("/factures")} className="text-[#0284C7] font-semibold">
              Tout voir
            </Button>
          </div>
          <div className="space-y-2">
            {(data?.recent_factures || []).length === 0 && <p className="text-slate-500 text-sm">Aucune facture.</p>}
            {(data?.recent_factures || []).map((f) => (
              <button
                key={f.id}
                data-testid={`dash-facture-${f.id}`}
                onClick={() => navigate(`/factures/${f.id}`)}
                className="w-full flex items-center justify-between p-3 rounded-md hover:bg-slate-50 text-left border border-slate-100"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{f.number}</div>
                  <div className="text-sm text-slate-500 truncate">{f.client?.name || "—"} · {dateFr(f.date_emission)}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge map={FACTURE_STATUS} status={f.status} />
                  <span className="font-bold text-slate-900">{eur(f.total_ttc)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
